import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { CustomerType, PaymentProfileType } from 'propTypes';
import {
  billingConfigs,
  paymentProfiles,
  customers,
  users
} from 'ducks';
import { BILLING_PAYMENT_METHODS, paymentMethodDisplayName } from 'components/payments/paymentInputs/PaymentSelector';
import {
  duckify,
  singleDuck,
  dependentDuck,
  filterDuck
} from '../../../utils/duckify';
import FontAwesome from '../../../utils/FA';
import SchoolContactInfo from '../sidebar/SchoolContactInfo';
import SideBar from '../sidebar/SideBar';
import NavBar from '../NavBar';
import ModalComponent from '../../ModalComponent';
import PaymentProfileForm from '../../payments/PaymentProfileForm';
import Placeholder from '../../Placeholder';

const PaymentProfile = (props) => {
  const {
    // duck meta
    allDucksReady,

    // ducks
    billingConfig,
    customer,
    savedProfiles,

    // actions
    afterSubmit,
    deleteProfile
  } = props;

  const [feedbackMessage, setFeedbackMessage] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editProfile, setEditProfile] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const paymentMethods = () => (
    customer.deposit_payment_methods.filter(method => BILLING_PAYMENT_METHODS.includes(method))
  );

  const paymentMethodName = (method) => paymentMethodDisplayName(customer, method);

  const needsVerify = (profile) => (
    profile.payment_verification && profile.payment_verification.status !== 'verified'
  );

  // If there is a single savedProfile and it requires action, set it as the edit profile
  useEffect(() => {
    if (savedProfiles.length === 1 && needsVerify(savedProfiles[0])) {
      setEditProfile(savedProfiles[0]);
    }
  }, [allDucksReady]);

  return (
    <div>
      <h2>
        <FontAwesome name='calendar-check-o' nbsp />
        Update Payment Details
      </h2>

      {
        feedbackMessage && (
          <div className={`alert alert-${feedbackMessage.type || 'info'}`}>
            {feedbackMessage.message}
            <button
              type='button'
              className='btn-close'
              aria-label="Close"
              onClick={() => setFeedbackMessage(null)}
            >
            </button>
          </div>
        )
      }

      {
        confirmDelete && (
          <ModalComponent
            className='delete-account-modal'
            onClose={() => setConfirmDelete(null)}
            buttons={[
              <button
                type='button'
                onClick={() => {
                  setDeleting(true);
                  deleteProfile(confirmDelete)
                    .then(() => {
                      setFeedbackMessage({
                        type: 'success',
                        message: `Your ${paymentMethodName(confirmDelete.payment_method)} information has been removed.`
                      });
                      setConfirmDelete(null);
                      setEditProfile(null);
                      setShowCreate(false);
                      setDeleting(false);
                    })
                    .catch((err) => {
                      setFeedbackMessage({
                        type: 'danger',
                        message: err.message || 'An error occurred. Please try again.'
                      });
                      setConfirmDelete(null);
                      setDeleting(false);
                    });
                }}
                className='btn btn-danger'
                disabled={deleting}
              >
                {deleting && <FontAwesome name='spinner' spin nbsp />}
                Delete Account Info
              </button>
            ]}
            title='Delete Account Info'
            closeText='Cancel'
          >
            Your saved account information will be deleted.  Are you sure?
          </ModalComponent>
        )
      }

      <Placeholder.loading ready={allDucksReady}>
        {
          savedProfiles.length > 0 && (
            <table className='table'>
              <thead>
                <tr>
                  <th>Payment Type</th>
                  <th>Account Name</th>
                  <th>{billingConfig.disable_autopay ? '' : 'Automatic Payments'}</th>
                  <th colSpan={2}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {savedProfiles.map(paymentProfile => (
                  <tr key={paymentProfile.id} className='payment-profile'>
                    <td>
                      {paymentProfile.payment_method === 'credit card' && <FontAwesome name='credit-card' nbsp />}
                      {paymentProfile.payment_method === 'electronic check' && <FontAwesome name='bank' nbsp />}
                      {paymentMethodName(paymentProfile.payment_method)}
                    </td>
                    <td>
                      {paymentProfile.test_mode && <span className={'badge bg-secondary'}>Test</span>}
                      {' '}
                      {paymentProfile.name}
                    </td>
                    <td>
                      <span>
                        {
                          !billingConfig.disable_autopay && (
                            paymentProfile.autopay ? (
                              <span className='text-success'>On</span>
                            ) : (
                              <span className='text-danger'>Off</span>
                            )
                          )
                        }
                      </span>
                    </td>
                    <td>
                      <button
                        type='button'
                        className='link-button edit-profile'
                        onClick={() => {
                          setShowCreate(false);
                          setEditProfile(paymentProfile);
                        }}
                      >
                        <FontAwesome name={needsVerify(paymentProfile) ? 'exclamation-circle' : 'edit'} nbsp />
                        {needsVerify(paymentProfile) ? 'Verify' : 'Edit'}
                      </button>
                    </td>
                    <td>
                      {!(billingConfig.require_autopay && paymentProfile.autopay) && (
                        <button
                          type='button'
                          className='link-button delete-profile'
                          onClick={() => {
                            setConfirmDelete(paymentProfile);
                          }}
                        >
                          <span className='text-danger'>
                            <FontAwesome name='trash' nbsp />
                            Remove
                          </span>
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3} />
                  <td colSpan={2}>
                    <button
                      type='button'
                      className='link-button add-profile'
                      onClick={() => {
                        setEditProfile(null);
                        setShowCreate(true);
                      }}
                    >
                      <span className='text-success'>
                        <FontAwesome name='plus-circle' nbsp />
                        {
                          (customer.cc_payment_gateway === 'StripeGateway')
                            ? 'Change Payment Method'
                            : 'Add New Payment Method'
                        }
                      </span>
                    </button>
                  </td>
                </tr>
              </tfoot>
            </table>
          )
        }

        {
          savedProfiles.length === 0 && (
            <p>Enter your account information below to save it for future purchases or enroll in AutoPay.</p>
          )
        }

        {
          (showCreate || editProfile || savedProfiles.length === 0) && (
            <div className='payment-profile-new-card'>
              <PaymentProfileForm
                afterSubmit={({ payment_profile }) => {
                  setShowCreate(false);
                  setEditProfile(null);

                  setFeedbackMessage({
                    type: 'success',
                    message: `Your ${paymentMethodName(payment_profile.payment_method)} information has been updated.`
                  });

                  afterSubmit();
                }}
                billingConfig={billingConfig}
                buttonText={(cust, method) => `Save ${paymentMethodName(method)} Info`}
                // this is gross, but it forces selectedPaymentMethod to reset when swapping between
                // "add new payment method" and updating a saved payment profile
                paymentMethods={editProfile ? [editProfile.payment_method] : paymentMethods()}
                paymentProfiles={editProfile ? [editProfile] : []} // disable loading existing profiles
                showNewPaymentOption={!editProfile}
              />
            </div>
          )
        }
      </Placeholder.loading>
    </div>
  );
};

PaymentProfile.propTypes = {
  // duck meta
  allDucksReady: PropTypes.bool.isRequired,

  // ducks
  billingConfig: PropTypes.shape({ disable_autopay: PropTypes.bool }).isRequired,
  customer: CustomerType.isRequired,
  savedProfiles: PropTypes.arrayOf(PaymentProfileType).isRequired,

  // actions
  afterSubmit: PropTypes.func.isRequired,
  deleteProfile: PropTypes.func.isRequired
};

const mapStateToDucks = () => {
  const customer = singleDuck(customers, 'current');
  const user = singleDuck(users, 'current');
  const billingConfig = singleDuck(billingConfigs, 'current');

  return {
    billingConfig,
    customer,
    user,
    savedProfiles: (
      dependentDuck(user, ({ value: currentUser }) => filterDuck(paymentProfiles, { user_id: currentUser.id }))
    )
  };
};

const mapDispatchToProps = (dispatch) => ({
  deleteProfile: (profile) => (
    dispatch(paymentProfiles.actions.remove(profile))
      .then(() => dispatch(paymentProfiles.actions.markDirty()))
  ),
  afterSubmit: () => dispatch(paymentProfiles.actions.markDirty())
});

export default {
  body: duckify(mapStateToDucks, () => ({}), mapDispatchToProps)(PaymentProfile),
  sideBar: () => (
    <SideBar>
      <SchoolContactInfo />
    </SideBar>
  ),
  navBar: () => (
    <NavBar
      links={[
        <Link href='/portal/billing'>
          <FontAwesome name='chevron-left' nbsp />
          Return to Student Billing Details
        </Link>
      ]}
    />
  )
};
